import { useContext } from 'react';

import { Box, Text } from '@betterment/samba-react';
import { Axis, DataContext } from '@visx/xychart';

import { useTickValues } from './useTickValues';
import { type AxisProps, DEFAULT_X_TICKS } from './utils';

import { useCursor } from '../cursor/useCursor';

import type { TickLabelProps, TickRendererProps } from '@visx/axis';

const CursorTickComponent = ({ formattedValue, x, y }: TickRendererProps) => {
  const { theme } = useContext(DataContext) ?? {};
  const tickLength = theme?.axisStyles.x.bottom.tickLength ?? 0;

  return (
    <foreignObject x={x} y={(y ?? 0) + tickLength} width={1} height={1} overflow="visible">
      <Box
        css={{
          display: 'inline-flex',
          transform: 'translate(-50%, -50%)',
          px: 'space.100',
          py: 'space.50',
          bg: 'background.neutral.strongest',
          borderRadius: 'medium',
          whiteSpace: 'nowrap',
        }}
      >
        <Text variant="text25" color="text.inverse">
          {formattedValue}
        </Text>
      </Box>
    </foreignObject>
  );
};

/**
 * Renders the horizontal (bottom) axis with tick labels and smart alignment.
 *
 * Automatically adjusts tick label alignment for the first and last ticks to prevent
 * overflow. When a cursor is active, displays only the cursor's tick value.
 *
 * The type parameter specifies the type of tick values passed to tickFormat.
 *
 * @example
 * ```tsx
 * type DataPoint = { date: Date; value: number };
 *
 * <XYChart width={500} height={300}>
 *   <XAxis<DataPoint['date']>
 *     numTicks={5}
 *     tickFormat={(date) => date.toLocaleDateString()}
 *   />
 *   <AreaSeries data={data} dataKey="series1" xAccessor={d => d.date} yAccessor={d => d.value} />
 * </XYChart>
 * ```
 */
export const XAxis = <T,>({ tickFormat, numTicks = DEFAULT_X_TICKS, tickComponent }: AxisProps<T>) => {
  const { xTickValues } = useTickValues({ numXTicks: numTicks });
  const lastIndex = xTickValues ? xTickValues.length - 1 : -1;

  const cursorData = useCursor();
  const data = useContext(DataContext);
  const cursorTickValue =
    cursorData.nearestKey && cursorData.data
      ? (data.dataRegistry?.get(cursorData.nearestKey)?.xAccessor(cursorData.data) as T)
      : undefined;
  const hasCursorTickValue = cursorTickValue !== undefined;

  // Band scale ticks do not start at the leftmost edge, so we don't adjust alignment
  const isBandScale = data.xScale && 'bandwidth' in data.xScale;

  const tickLabelProps: TickLabelProps<T> = (_value, index) => ({
    dy: 4,
    ...(!isBandScale && !hasCursorTickValue && index === 0 && { textAnchor: 'start' as const }),
    ...(!isBandScale && !hasCursorTickValue && index === lastIndex && { textAnchor: 'end' as const }),
  });

  return (
    <Axis
      orientation="bottom"
      tickValues={hasCursorTickValue ? [cursorTickValue] : xTickValues}
      tickFormat={tickFormat}
      hideTicks={true}
      tickLabelProps={tickLabelProps}
      tickComponent={hasCursorTickValue ? CursorTickComponent : tickComponent}
    />
  );
};
